import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * "Confirm you're a person" on the sign-in and sign-up forms. The server asks
 * for it only when something looks wrong (personCheck.js in the API), by naming
 * the widget's key on the error, so nothing loads until then. The widget is
 * Cloudflare Turnstile; each token works once.
 */

type Turnstile = {
  render: (
    box: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    }
  ) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        loading = null;
        script.remove();
        reject(new Error('The check didn’t load'));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
}

/** The widget's key, when this error is the server asking for the check. */
export function personCheckSiteKey(err: unknown): string | null {
  const errors = (err as { graphQLErrors?: ReadonlyArray<{ extensions?: any }> } | null)
    ?.graphQLErrors;
  for (const e of errors || []) {
    const key = e.extensions?.personCheck?.siteKey;
    if (typeof key === 'string' && key) return key;
  }
  return null;
}

function PersonCheckWidget({
  siteKey,
  round,
  onToken,
}: {
  siteKey: string;
  round: number;
  onToken: (token: string) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let gone = false;
    loadTurnstile()
      .then(() => {
        if (gone || !box.current || !window.turnstile) return;
        widget.current = window.turnstile.render(box.current, {
          sitekey: siteKey,
          callback: (token) => {
            setFailed(false);
            onToken(token);
          },
          'expired-callback': () => onToken(''),
          'error-callback': () => {
            setFailed(true);
            onToken('');
          },
        });
      })
      .catch(() => {
        if (!gone) setFailed(true);
      });
    return () => {
      gone = true;
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey, onToken]);

  // A token works once: every try after the first needs a new one.
  useEffect(() => {
    if (round > 0 && widget.current) window.turnstile?.reset(widget.current);
  }, [round]);

  return (
    <div className="flow-field">
      <div ref={box} />
      {failed ? (
        <span className="flow-error">
          The check couldn’t load. Refresh the page, or turn off any content blocker.
        </span>
      ) : null}
    </div>
  );
}

export function usePersonCheck() {
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [round, setRound] = useState(0);

  /** After a try that failed: shows the check if the server asked, and spends the token. */
  const failed = useCallback((err: unknown) => {
    const key = personCheckSiteKey(err);
    if (key) setSiteKey(key);
    setToken('');
    setRound((r) => r + 1);
  }, []);

  /** After a try that worked: the server asks again if it needs to. */
  const passed = useCallback(() => {
    setSiteKey(null);
    setToken('');
  }, []);

  return {
    /** Spread into the mutation's variables. */
    variables: token ? { personCheck: token } : {},
    /** The check is showing and isn't finished: sending now would only be refused. */
    waiting: Boolean(siteKey && !token),
    failed,
    passed,
    widget: siteKey ? (
      <PersonCheckWidget siteKey={siteKey} round={round} onToken={setToken} />
    ) : null,
  };
}
